import { useTranslation } from 'react-i18next';
import logixPanelInstance from 'i18n';
import {
  StyledClampText,
  StyledTableCell
} from 'components/DataTable/style';
import {
  COMMENT_LOOKUP,
  EXP_LOOKUP,
  AVG_DEPOSIT_LOOKUP,
  lookupValue,
  ONE_MONTH_NEW_ACC_LOOKUP,
  PER_MONTH_TRADING_VOLUME_LOOKUP,
  REVIEW_STATUS_LOOKUP,
  SYNC_STATUS_LOOKUP
} from '../../utils/lookups';
import {
  MLAApplicationComment,
  MLAApplicationRowData,
  MLAApplicationStatus
} from '../../hooks/useGetMLAApplicationList';
import { Style } from './style';

export type TableRowProps = {
  rowData: MLAApplicationRowData;
  canEdit: boolean;
  isRowSubmitting: boolean;
  onReview: (value: MLAApplicationRowData) => void;
  onRetry: (value: MLAApplicationRowData) => void;
};

const isShowReviewBtn = (
  status: MLAApplicationStatus
): boolean => status === MLAApplicationStatus.Pending;

const isShowRetryBtn = (rowData: MLAApplicationRowData): boolean => (
  rowData.status === MLAApplicationStatus.Approve &&
  (rowData.syncStatus === 'SYNC_FAILED' || rowData.syncStatus === null)
);

const TableRow = (props: TableRowProps): JSX.Element => {
  const {
    rowData, canEdit, isRowSubmitting, onReview, onRetry
  } = props;

  const { t } = useTranslation('mla', {
    i18n: logixPanelInstance
  });

  const isActionDisabled = !canEdit || isRowSubmitting;

  return (
    <Style.TableRow $status={rowData.status}>
      <StyledTableCell $width="60px" $stickyLeft="0px" aria-label="no column">
        {rowData.id}
      </StyledTableCell>
      <StyledTableCell
        $stickyLeft="60px"
        $boxShadowPosition="right"
        align="left"
        aria-label="trader number column"
      >
        {rowData.traderId}
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="date column">
        {rowData.applicationDate}
      </StyledTableCell>
      <StyledTableCell
        align="left"
        aria-label="email column"
        $shouldEllipsis
      >
        {rowData.email}
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="exp column">
        {lookupValue(EXP_LOOKUP, rowData.exp)}
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="exp company column" $clampText>
        <StyledClampText>{rowData.expCompany}</StyledClampText>
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="one month new acc column">
        {lookupValue(ONE_MONTH_NEW_ACC_LOOKUP, rowData.oneMonthNewAcc)}
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="per month trading volume column">
        {lookupValue(PER_MONTH_TRADING_VOLUME_LOOKUP, rowData.perMonthTradingVolume)}
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="avg deposit column">
        {lookupValue(AVG_DEPOSIT_LOOKUP, rowData.avgDeposit)}
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="exp comment column" $clampText>
        <StyledClampText>{rowData.expComment}</StyledClampText>
      </StyledTableCell>
      <StyledTableCell align="left" aria-label="service comment column" $clampText>
        <StyledClampText>{rowData.serviceComment}</StyledClampText>
      </StyledTableCell>
      <StyledTableCell
        $width="135px"
        align="left"
        aria-label="review status column"
      >
        <Style.ReviewStatusContent>
          <Style.StatusCircle
            viewBox="0 0 100 100"
            xmlns="http://www.w3.org/2000/svg"
            status={rowData.status}
          >
            <circle cx="50" cy="50" r="50" fill="currentColor" />
          </Style.StatusCircle>
          {t(lookupValue(REVIEW_STATUS_LOOKUP, rowData.status))}
        </Style.ReviewStatusContent>
      </StyledTableCell>
      <StyledTableCell
        $width="120px"
        align="center"
        aria-label="action column"
      >
        {isShowReviewBtn(rowData.status) ? (
          <Style.ReviewButton
            type="button"
            disabled={isActionDisabled}
            onClick={() => onReview(rowData)}
          >
            {t('review')}
          </Style.ReviewButton>
        ) :
          (
            <Style.ReviewedText>{t('reviewed')}</Style.ReviewedText>
          )}
      </StyledTableCell>
      <StyledTableCell
        $width="120px"
        align="center"
        aria-label="action column"
      >
        {rowData.comment === MLAApplicationComment.None ?
          '-' :
          t(lookupValue(COMMENT_LOOKUP, rowData.comment))}
      </StyledTableCell>
      <StyledTableCell
        $width="120px"
        align="center"
        aria-label="account status column"
      >
        {rowData.syncStatus ? (
          <Style.AccountStatus $syncStatus={rowData.syncStatus}>
            {t(lookupValue(SYNC_STATUS_LOOKUP, rowData.syncStatus))}
          </Style.AccountStatus>
        ) : (
          '-'
        )}
      </StyledTableCell>
      <StyledTableCell
        $width="120px"
        align="center"
        aria-label="retry column"
      >
        {isShowRetryBtn(rowData) ? (
          <Style.RetryButton
            type="button"
            disabled={isActionDisabled}
            onClick={() => onRetry(rowData)}
          >
            {t('retry')}
          </Style.RetryButton>
        ) : (
          '-'
        )}
      </StyledTableCell>
    </Style.TableRow>
  );
};

export default TableRow;
